import React, { useState, useEffect, useCallback } from 'react';
import {
  Typography,
  Grid,
  Paper,
  Button,
  Stack,
  Box,
  Card,
  CardContent,
  CardActions,
  Chip,
  CircularProgress,
  Alert,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle
} from '@mui/material';
import {
  People as PeopleIcon,
  Groups as TeamsIcon,
  EmojiEvents as TrophyIcon,
  CalendarToday as ScheduleIcon,
  SportsSoccer as GamesIcon,
  Refresh as RefreshIcon,
  HowToReg as AvailabilityIcon,
  Sports as SportsIcon,
  AppRegistration as RegistrationIcon,
  Settings as ConfigIcon,
  Email as EmailIcon,
  PhotoCamera as PhotoCameraIcon,
  Upload as UploadIcon,
  EmojiEvents as PlayoffsIcon,
  Warning as ConflictIcon,
  Description as ReportIcon,
  HowToReg as DraftIcon,
  Forum as ChatIcon,
  EventNote as ScheduleGeneratorIcon,
} from '@mui/icons-material';
import CsvUpload from '../../../components/upload/CsvUpload';
import AddGameDialog from '../../../components/game/AddGameDialog';
import DraftViewersManager from '../../../components/admin/league/DraftViewersManager';
import { useParams, Link } from 'react-router-dom';
import AdminLayout from '../../../layouts/AdminLayout';
import unifiedApi from '../../../utils/unifiedApi';
import { usePermissions, useCanManageLeague, useCanViewLeagueDraft } from '../../../contexts/PermissionsContext';

const LeagueAdminDashboard = () => {
  const { leagueId } = useParams();
  const [leagueData, setLeagueData] = useState({
    name: '',
    slug: '',
    season: '',
    status: 'active'
  });
  const [stats, setStats] = useState({
    totalMembers: 0,
    totalTeams: 0,
    totalGames: 0,
    gamesPlayed: 0,
    gamesScheduled: 0
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [actionError, setActionError] = useState(null);
  const [actionSuccess, setActionSuccess] = useState(null);
  const [confirmAction, setConfirmAction] = useState(null);
  const [actionLoading, setActionLoading] = useState(null);
  const [csvUploadOpen, setCsvUploadOpen] = useState(false);
  const [addGameOpen, setAddGameOpen] = useState(false);
  const { isLoading: permissionsLoading } = usePermissions();
  const canManageLeague = useCanManageLeague(parseInt(leagueId));
  const canViewLeagueDraft = useCanViewLeagueDraft(parseInt(leagueId));

  const maintenanceActions = {
    rebuildStandings: {
      title: 'Rebuild Standings',
      description: 'This will recalculate regular-season standings from finalized game scores. Continue?',
      loadingLabel: 'Rebuilding...',
      successMessage: (result) => `Standings rebuilt from ${result?.gamesProcessed ?? 0} finalized regular-season games.`,
      errorMessage: 'Failed to rebuild standings.',
      run: () => unifiedApi.rebuildLeagueStandings(leagueId),
    },
    recalculatePlayoffs: {
      title: 'Recalculate Playoff Standings',
      description: 'This will recalculate playoff pool standings from playoff game scores. Continue?',
      loadingLabel: 'Recalculating...',
      successMessage: () => 'Playoff standings recalculated.',
      errorMessage: 'Failed to recalculate playoff standings.',
      run: () => unifiedApi.recalculatePlayoffStandings(leagueId),
    },
  };

  const fetchLeagueData = useCallback(async () => {
    if (!leagueId) return;

    setLoading(true);
    setError(null);

    try {
      // Fetch league basic info and stats in parallel
      const [leagueInfo, statsData] = await Promise.all([
        unifiedApi.getLeagueById(leagueId),
        unifiedApi.getLeagueStats(leagueId)
      ]);

      setLeagueData({
        name: leagueInfo.name,
        slug: leagueInfo.slug || '',
        season: leagueInfo.season || 'Spring 2025',
        status: 'active'
      });

      setStats({
        totalMembers: statsData.totalUsers,
        totalTeams: statsData.totalTeams,
        totalGames: statsData.totalGames,
        gamesPlayed: statsData.completedGames,
        gamesScheduled: statsData.scheduledGames
      });

      setLoading(false);
    } catch (err) {
      console.error('Failed to fetch league data', err);
      setError('Failed to load league data. Please try again.');
      setLoading(false);
      
      // Fallback to placeholder data on error
      setStats({
        totalMembers: 0,
        totalTeams: 0,
        totalGames: 0,
        gamesPlayed: 0,
        gamesScheduled: 0
      });
    }
  }, [leagueId]);

  useEffect(() => {
    fetchLeagueData();
  }, [fetchLeagueData]);

  const handleConfirmMaintenanceAction = async () => {
    if (!confirmAction) return;

    const action = maintenanceActions[confirmAction];
    setActionLoading(confirmAction);
    setActionError(null);
    setActionSuccess(null);

    try {
      const result = await action.run();
      setActionSuccess(action.successMessage(result));
      setConfirmAction(null);
    } catch (err) {
      console.error(action.errorMessage, err);
      setActionError(action.errorMessage);
      setConfirmAction(null);
    } finally {
      setActionLoading(null);
    }
  };

  const statCards = [
    {
      title: 'League Members',
      value: stats.totalMembers,
      icon: <PeopleIcon sx={{ fontSize: 40, color: '#2E4156' }} />,
      color: '#e3f2fd',
      link: `/admin/leagues/${leagueId}/members`
    },
    {
      title: 'Teams',
      value: stats.totalTeams,
      icon: <TeamsIcon sx={{ fontSize: 40, color: '#388e3c' }} />,
      color: '#e8f5e8',
      link: `/admin/leagues/${leagueId}/teams`
    },
    {
      title: 'Total Games',
      value: stats.totalGames,
      icon: <GamesIcon sx={{ fontSize: 40, color: '#9c27b0' }} />,
      color: '#f3e5f5',
    },
    {
      title: 'Completed Games',
      value: stats.gamesPlayed,
      icon: <TrophyIcon sx={{ fontSize: 40, color: '#f57c00' }} />,
      color: '#fff3e0',
    },
    {
      title: 'Scheduled Games',
      value: stats.gamesScheduled,
      icon: <ScheduleIcon sx={{ fontSize: 40, color: '#7b1fa2' }} />,
      color: '#f3e5f5',
    }
  ];

  return (
    <AdminLayout>
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
          <CircularProgress size={60} />
        </Box>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {actionError && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setActionError(null)}>
          {actionError}
        </Alert>
      )}

      {actionSuccess && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setActionSuccess(null)}>
          {actionSuccess}
        </Alert>
      )}

      {!loading && !error && (
        <>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
            <Box>
              <Typography variant="h4" gutterBottom>
                {leagueData.name} Administration
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography variant="body1" color="text.secondary">
              {leagueData.season}
            </Typography>
            <Chip 
              label={leagueData.status} 
              color={leagueData.status === 'active' ? 'success' : 'default'}
              size="small"
            />
          </Box>
        </Box>
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={fetchLeagueData}
          disabled={loading}
        >
          Refresh Stats
        </Button>
      </Box>

      {/* Statistics Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {statCards.map((card, index) => (
          <Grid size={{ xs: 12, sm: 6, md: 4, lg: 2.4 }} key={index}>
            <Card sx={{ height: '100%', bgcolor: card.color }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  {card.icon}
                  <Box sx={{ ml: 2 }}>
                    <Typography variant="h4" component="div">
                      {card.value}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {card.title}
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
              {card.link && (
              <CardActions>
                <Button 
                  size="small" 
                  component={Link}
                  to={card.link}
                >
                  Manage
                </Button>
              </CardActions>
              )}
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Quick Actions */}
      <Grid container spacing={3}>
        <Grid size={{ xs: 12, md: 6 }}>
          <Stack spacing={3}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                League Management
              </Typography>
              <Stack spacing={2}>
                <Button
                  variant="outlined"
                  component={Link}
                  to={`/admin/leagues/${leagueId}/availability`}
                  startIcon={<AvailabilityIcon />}
                >
                  Player Availability
                </Button>
                <Button
                  variant="outlined"
                  component={Link}
                  to={`/admin/leagues/${leagueId}/attendance`}
                  startIcon={<PeopleIcon />}
                >
                  Player Attendance Report
                </Button>
                <Button
                  variant="outlined"
                  component={Link}
                  to={`/admin/leagues/${leagueId}/disciplinary`}
                  startIcon={<SportsIcon />}
                  color="warning"
                >
                  Disciplinary Report (Cards)
                </Button>
                <Button
                  variant="outlined"
                  component={Link}
                  to={`/admin/leagues/${leagueId}/profile-photos`}
                  startIcon={<PhotoCameraIcon />}
                >
                  Profile Photo Report
                </Button>
              </Stack>
            </Paper>

            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                Registration
              </Typography>
              <Stack spacing={2}>
                {!permissionsLoading && canManageLeague && (
                  <Button
                    variant="outlined"
                    component={Link}
                    to={`/admin/leagues/${leagueId}/registration-config`}
                    startIcon={<ConfigIcon />}
                  >
                    Registration Configuration
                  </Button>
                )}
                <Button
                  variant="outlined"
                  component={Link}
                  to={`/admin/leagues/${leagueId}/registrations`}
                  startIcon={<RegistrationIcon />}
                >
                  Registration Dashboard
                </Button>
              </Stack>
            </Paper>

            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                Communications
              </Typography>
              <Stack spacing={2}>
                <Button
                  variant="outlined"
                  component={Link}
                  to={`/admin/leagues/${leagueId}/reminder-emails`}
                  startIcon={<EmailIcon />}
                >
                  Reminder Email Dashboard
                </Button>
              </Stack>
            </Paper>
          </Stack>
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <Stack spacing={3}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                Competition Operations
              </Typography>
              <Stack spacing={2}>
                {!permissionsLoading && canManageLeague && (
                  <Button
                    variant="outlined"
                    onClick={() => setCsvUploadOpen(true)}
                    startIcon={<UploadIcon />}
                  >
                    Upload Game CSV
                  </Button>
                )}
                {!permissionsLoading && canManageLeague && (
                  <Button
                    variant="outlined"
                    onClick={() => setAddGameOpen(true)}
                    startIcon={<GamesIcon />}
                  >
                    Add Game
                  </Button>
                )}
                {!permissionsLoading && canManageLeague && (
                  <Button
                    variant="outlined"
                    component={Link}
                    to={`/admin/leagues/${leagueId}/schedule-generator`}
                    startIcon={<ScheduleGeneratorIcon />}
                  >
                    Schedule Generator
                  </Button>
                )}
                {!permissionsLoading && canManageLeague && (
                  <Button
                    variant="outlined"
                    component={Link}
                    to={`/admin/leagues/${leagueId}/teams`}
                    startIcon={<TeamsIcon />}
                  >
                    Team Management
                  </Button>
                )}
                <Button
                  variant="outlined"
                  component={Link}
                  to={`/admin/leagues/${leagueId}/match-reports`}
                  startIcon={<ReportIcon />}
                >
                  Match Reports
                </Button>
                <Button
                  variant="outlined"
                  component={Link}
                  to={`/admin/leagues/${leagueId}/match-report-conflicts`}
                  startIcon={<ConflictIcon />}
                  color="warning"
                >
                  Score Conflicts
                </Button>
                {!permissionsLoading && canManageLeague && (
                  <Button
                    variant="outlined"
                    component={Link}
                    to={`/admin/leagues/${leagueId}/playoffs`}
                    startIcon={<PlayoffsIcon />}
                  >
                    Playoffs
                  </Button>
                )}
                {!permissionsLoading && canManageLeague && (
                  <Button
                    variant="outlined"
                    onClick={() => setConfirmAction('rebuildStandings')}
                    startIcon={<RefreshIcon />}
                    disabled={actionLoading !== null}
                  >
                    {actionLoading === 'rebuildStandings' ? 'Rebuilding...' : 'Rebuild Standings'}
                  </Button>
                )}
                {!permissionsLoading && canManageLeague && (
                  <Button
                    variant="outlined"
                    onClick={() => setConfirmAction('recalculatePlayoffs')}
                    startIcon={<PlayoffsIcon />}
                    disabled={actionLoading !== null}
                  >
                    {actionLoading === 'recalculatePlayoffs' ? 'Recalculating...' : 'Recalculate Playoff Standings'}
                  </Button>
                )}
              </Stack>
            </Paper>

            {!permissionsLoading && canManageLeague && (
              <Paper sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom>
                  Chat
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Turn chat on for this league and manage its league and team channels.
                </Typography>
                <Button
                  variant="outlined"
                  component={Link}
                  to={`/admin/chat?leagueId=${leagueId}`}
                  startIcon={<ChatIcon />}
                  fullWidth
                >
                  Manage Chat
                </Button>
              </Paper>
            )}

            {!permissionsLoading && (canManageLeague || canViewLeagueDraft) && (
              <Paper sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom>
                  Draft
                </Typography>
                {canManageLeague ? (
                  <DraftViewersManager leagueId={leagueId} leagueSlug={leagueData.slug} />
                ) : leagueData.slug && (
                  <Button
                    variant="outlined"
                    component={Link}
                    to={`/leagues/${leagueData.slug}/draft`}
                    startIcon={<DraftIcon />}
                    fullWidth
                  >
                    Open Draft Board
                  </Button>
                )}
              </Paper>
            )}
          </Stack>
        </Grid>
      </Grid>

      <CsvUpload
        leagueId={leagueId}
        open={csvUploadOpen}
        onClose={() => setCsvUploadOpen(false)}
        onSuccess={fetchLeagueData}
      />

      <AddGameDialog
        leagueId={leagueId}
        open={addGameOpen}
        onClose={() => setAddGameOpen(false)}
        onSuccess={() => {
          setActionSuccess('Game added successfully.');
          fetchLeagueData();
        }}
      />

      <Dialog
        open={confirmAction !== null}
        onClose={() => actionLoading === null && setConfirmAction(null)}
      >
        <DialogTitle>{confirmAction ? maintenanceActions[confirmAction].title : ''}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {confirmAction ? maintenanceActions[confirmAction].description : ''}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmAction(null)} disabled={actionLoading !== null}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleConfirmMaintenanceAction}
            disabled={actionLoading !== null}
          >
            {confirmAction && actionLoading === confirmAction
              ? maintenanceActions[confirmAction].loadingLabel
              : 'Continue'}
          </Button>
        </DialogActions>
      </Dialog>
        </>
      )}
    </AdminLayout>
  );
};

export default LeagueAdminDashboard;
